import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent
} from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { ConnectedUserService } from 'app/core/user/connected-user.service';
import { User } from '../../../../shared/models/actors-users/user.model';

@Component({
  selector: 'classy-layout',
  templateUrl: './classy.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
    /* Highlight matched navigation items */
    .search-match {
      background-color: rgba(241, 245, 249, 0.06); /* slate-100 at low opacity */
      border-left: 3px solid #f1f5f9; /* requested border color */
    }
    .search-match .mat-icon, .search-match .icon, .search-match .title, .search-match .subtitle {
      color: #ffffff !important;
    }
    
    /* Navigation layout improvements */
    .fuse-vertical-navigation {
      display: flex !important;
      flex-direction: column !important;
    }
    
    .fuse-vertical-navigation-content {
      display: flex !important;
      flex-direction: column !important;
      height: 100% !important;
    }
    
    .fuse-vertical-navigation-content-header {
      flex-shrink: 0 !important;
    }
    
    /* Make only the navigation items scrollable, not the header */
    .fuse-vertical-navigation-wrapper {
      display: flex !important;
      flex-direction: column !important;
      height: 100vh !important;
    }
    
    .fuse-vertical-navigation-header {
      flex-shrink: 0 !important;
    }
    
    /* The navigation content should be scrollable for items only */
    .fuse-vertical-navigation-content {
      flex: 1 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
    }
    
    /* Fixed header with search */
    .fuse-vertical-navigation-content-header {
      flex-shrink: 0 !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 10 !important;
      background-color: rgb(17 24 39) !important; /* bg-gray-900 */
    }
    
    /* Scrollable items container */
    .fuse-vertical-navigation-items {
      flex: 1 !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
    }

    fuse-vertical-navigation-basic-item {
      max-height: 44px;
    }
    `
  ]
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation;
  connectedUser: User;
  readonly environment = environment;
  // Search state and filtered navigation
  searchQuery: string = '';
  currentNavigationItems: any[] = [];
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  // Menu state: 0 = expanded (default), 1 = icons only (dense), 2 = hidden (closed)
  menuState: 0 | 1 | 2 = 0;
  // Hover state for vertical navigation (used to temporarily show full content when dense)
  isNavHover: boolean = false;
  
  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _userService: ConnectedUserService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
        // Initialize current items with default navigation on first load
        this.currentNavigationItems = this.navigation?.default || [];
        this._fuseNavigationService
          .getComponent<FuseVerticalNavigationComponent>('mainNavigation')
          ?.refresh();
      });

    // Subscribe to the user service
    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      this.connectedUser = user;
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    // Clear search and restore full navigation when a route is activated (user clicked a nav item)
    this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.searchQuery) {
          this.clearSearch();
        }
      }
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      if (this.isScreenSmall) {
        // Sur mobile, comportement classique ouvert/fermé
        navigation.toggle();
        return;
      }

      // Cycle 0 -> 1 -> 2 -> 0
      // 0 = tout affiché ('default', opened = true)
      // 1 = que les icônes ('dense', opened = true)
      // 2 = masqué (opened = false)
      this.menuState = ((this.menuState + 1) % 3) as 0 | 1 | 2;
      
      if (this.menuState === 2) {
        navigation.close();
      } else {
        navigation.open();
      }
    }
  }

  // Hover handlers for navigation
  onNavMouseEnter(): void {
    this.isNavHover = true;
  }

  onNavMouseLeave(): void {
    this.isNavHover = false;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Navigation search
  // -----------------------------------------------------------------------------------------------------

  onSearchQueryChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery = value ?? '';
    const term = this.searchQuery.trim().toLowerCase();
    if (!term) {
      this.currentNavigationItems = this.navigation?.default || [];
    } else {
      this.currentNavigationItems = this._filterNavigationItems(
        this.navigation?.default || [],
        term
      );
    }
    this._fuseNavigationService
      .getComponent<FuseVerticalNavigationComponent>('mainNavigation')
      ?.refresh();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.currentNavigationItems = this.navigation?.default || [];
    this._fuseNavigationService
      .getComponent<FuseVerticalNavigationComponent>('mainNavigation')
      ?.refresh();
  }

  private _filterNavigationItems(items: any[], term: string): any[] {
    const result: any[] = [];
    for (const item of items || []) {
      const originalTitle: string = (item.title || '').toString();
      const originalSubtitle: string = (item.subtitle || '').toString();
      const titleLc: string = originalTitle.toLowerCase();
      const subtitleLc: string = originalSubtitle.toLowerCase();
      const children: any[] = Array.isArray(item.children) ? item.children : [];

      const matchesSelf = titleLc.includes(term) || subtitleLc.includes(term);

      // Filter children recursively
      const filteredChildren = this._filterNavigationItems(children, term);

      // Include current item if it matches or has matching children
      if (matchesSelf || filteredChildren.length > 0) {
        const copy: any = { ...item };
        if (filteredChildren.length > 0) {
          copy.children = filteredChildren;
          copy.expanded = true; // Auto-expand items with matching children
        } else if (copy.children) {
          copy.children = [];
        }
        if (matchesSelf) {
          const existingClasses = (copy.classes || '').toString().trim();
          copy.classes = (existingClasses ? existingClasses + ' ' : '') + 'search-match';
          copy._highlightedTitle = this._buildHighlightedHtml(originalTitle, term);
          if (originalSubtitle) {
            copy._highlightedSubtitle = this._buildHighlightedHtml(originalSubtitle, term);
          }
          copy.expanded = true; // Auto-expand matching items
        }
        result.push(copy);
      }
    }
    return result;
  }

  private _escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private _buildHighlightedHtml(text: string, term: string): string {
    if (!term) return this._escapeHtml(text);
    const esc = this._escapeHtml(text);
    try {
      const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
      return esc.replace(re, '<mark class="px-0.5 rounded bg-accent text-white">$1</mark>');
    } catch {
      return esc;
    }
  }
}
